import type { TripSummaryWithPermission } from "@poi-planner/shared";
import { api } from "./api";
import * as offlineStore from "./offlineStore";
import type { SavedTrip } from "./offlineStore";
import { exportTravelRoutes } from "./travelService";
import { useAuthStore } from "../store/authStore";
import { useTripsListStore } from "../store/tripsListStore";
import { useLibraryStore } from "../store/libraryStore";
import { useLegStore } from "../store/legStore";
import {
  flushTripSave,
  resumeTripSave,
  useTripStore,
} from "../store/tripStore";
import { resumeLibrarySave } from "../store/libraryStore";
import {
  isOfflineMode,
  onReconnect,
  setSavedAt,
  useConnectivityStore,
} from "../store/connectivityStore";

/**
 * Offline Sync
 *
 * Keeps the on-device copy (offlineStore) current while the app is online:
 * the trip list and library already loaded at start-up, every trip whose
 * revision has moved on, and the open trip after each successful save.
 */

const FETCH_CONCURRENCY = 3;
const STALE_SYNC_MS = 15 * 60 * 1000;
const ACTIVE_TRIP_WRITE_DELAY_MS = 1_000;

let lastSyncStartedAt = 0;
let runningSync: Promise<void> | null = null;
let persistRequested = false;
// Set by sign-out so an in-flight sync cannot recreate the deleted copy.
let writesBlocked = false;

function signedInUserId(): string | null {
  const { status, userId } = useAuthStore.getState();
  return status === "authenticated" ? userId : null;
}

/** Can this session write the device copy? Not offline, and signed in. */
function canWrite(): string | null {
  if (writesBlocked || isOfflineMode()) return null;
  return signedInUserId();
}

function isCurrent(
  saved: SavedTrip | undefined,
  summary: TripSummaryWithPermission,
): boolean {
  if (!saved) return false;
  if (saved.permission !== summary.permission) return false;
  return summary.revision != null
    ? saved.trip.revision === summary.revision
    : saved.trip.updatedAt === summary.updatedAt;
}

async function fetchTrip(
  summary: TripSummaryWithPermission,
): Promise<SavedTrip | null> {
  const [data, pinnedPoiIds] = await Promise.all([
    api.loadTripById(summary.id),
    api.getPinnedPoiIds(summary.id),
  ]);
  if (!data) return null;
  return {
    trip: data.trip,
    schedule: data.schedule,
    legs: data.legs,
    permission: summary.permission,
    pinnedPoiIds,
    savedAt: new Date().toISOString(),
  };
}

async function runPool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.min(limit, items.length) }, () =>
    (async () => {
      while (next < items.length) {
        await worker(items[next++]);
      }
    })(),
  );
  await Promise.all(lanes);
}

async function markSynced(): Promise<void> {
  const now = new Date().toISOString();
  await offlineStore.setLastSyncedAt(now);
  setSavedAt(now);
}

async function doSync(userId: string): Promise<void> {
  if (!(await offlineStore.prepareForUser(userId))) return;

  // The list and library are already in memory from start-up.
  const { trips } = useTripsListStore.getState();
  const { pois, tags, categoryTags } = useLibraryStore.getState();
  await Promise.all([
    offlineStore.saveTripsList(trips),
    offlineStore.saveLibrary({ pois, tags, categoryTags }),
  ]);

  const saved = new Map(
    (await offlineStore.getAllTrips()).map((record) => [
      record.trip.id,
      record,
    ]),
  );
  const stale = trips.filter(
    (summary) => !isCurrent(saved.get(summary.id), summary),
  );

  let failed = false;
  await runPool(stale, FETCH_CONCURRENCY, async (summary) => {
    if (canWrite() !== userId) return;
    try {
      const record = await fetchTrip(summary);
      if (!record || canWrite() !== userId) return;
      const previous = saved.get(summary.id);
      if (previous?.travelRoutes) record.travelRoutes = previous.travelRoutes;
      await offlineStore.saveTrip(record);
    } catch (error: unknown) {
      failed = true;
      console.warn(`Could not save trip ${summary.id} for offline use`, error);
    }
  });

  await offlineStore.pruneTrips(trips.map((trip) => trip.id));

  if (!persistRequested && navigator.storage?.persist) {
    persistRequested = true;
    void navigator.storage.persist().catch(() => false);
  }

  if (!failed) await markSynced();
}

/** Bring the whole device copy up to date. Safe to call repeatedly. */
export function syncAll(): Promise<void> {
  if (runningSync) return runningSync;
  const userId = canWrite();
  if (!userId) return Promise.resolve();
  lastSyncStartedAt = Date.now();
  runningSync = doSync(userId)
    .catch((error: unknown) => {
      console.warn("Offline sync failed", error);
    })
    .finally(() => {
      runningSync = null;
    });
  return runningSync;
}

/** Write the open trip from memory, with no extra fetch. */
async function saveActiveTrip(): Promise<void> {
  const userId = canWrite();
  const { trip, schedule, permission, pinnedPoiIds, saveStatus } =
    useTripStore.getState();
  // Unsaved edits are not on the server yet; the next save writes them.
  if (!userId || !trip || !permission || saveStatus !== "saved") return;
  if (!(await offlineStore.prepareForUser(userId))) return;

  const now = new Date().toISOString();
  await offlineStore.saveTrip({
    trip,
    schedule,
    legs: useLegStore.getState().legs,
    permission,
    pinnedPoiIds: [...pinnedPoiIds],
    travelRoutes: exportTravelRoutes(),
    savedAt: now,
  });
  await offlineStore.saveTripsList(useTripsListStore.getState().trips);
  setSavedAt(now);
}

/**
 * Claim the device copy for the signed-in user, wiping another user's copy
 * first (a second person signing in on this device).
 */
export async function prepareOfflineCopy(userId: string): Promise<void> {
  writesBlocked = false;
  await offlineStore.prepareForUser(userId);
}

/** Delete the device copy and stop writing to it (sign-out). */
export async function clearOfflineCopy(): Promise<void> {
  writesBlocked = true;
  await runningSync;
  await offlineStore.clearAll();
  setSavedAt(null);
}

let started = false;

/**
 * Wire the triggers: the open trip after each save or granular write, the
 * whole copy when the connection returns or a stale tab becomes visible.
 */
export function startOfflineSync(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  let writeTimer: ReturnType<typeof setTimeout> | null = null;
  useTripStore.subscribe((state, prev) => {
    const trip = state.trip;
    if (!trip) return;
    const changed =
      trip.id !== prev.trip?.id ||
      trip.revision !== prev.trip?.revision ||
      (state.saveStatus === "saved" && prev.saveStatus !== "saved");
    if (!changed) return;
    if (writeTimer) clearTimeout(writeTimer);
    writeTimer = setTimeout(() => {
      writeTimer = null;
      void saveActiveTrip().catch((error: unknown) => {
        console.warn("Could not save the open trip for offline use", error);
      });
    }, ACTIVE_TRIP_WRITE_DELAY_MS);
  });

  onReconnect(() => {
    // Edits made before the connection dropped are still queued.
    void Promise.all([resumeTripSave(), resumeLibrarySave()])
      .then(() => flushTripSave())
      .finally(() => {
        void useTripsListStore
          .getState()
          .refreshTrips()
          .then(() => syncAll());
      });
  });

  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "visible" &&
      lastSyncStartedAt > 0 &&
      !useConnectivityStore.getState().startedOffline &&
      Date.now() - lastSyncStartedAt > STALE_SYNC_MS
    ) {
      void useTripsListStore
        .getState()
        .refreshTrips()
        .then(() => syncAll());
    }
  });
}
